import inputBuilder from 'common/helpers/input-builder'
import CoursePlanBuilder from 'client/js/models/course-plan-builder'
import StudentPreferences from 'client/js/models/student-preferences.js'
import objectHash from 'object-hash'
import CoursePlanStore from 'client/js/models/course-plan-store'


function getNewStateObj() {
   return {
      plan: null,
      hash: undefined,
      lockedUnits: [],
      studentNotes: ''
   }
}

const getters = {

   getInput: (state, getters, rootState, rootGetters) => {
      const selectedPreferences = rootGetters['preferences/getSelectedStudyPreferences']
      const course = rootGetters['courses/getSelectedCourse']
      const defaultTeachingScheduled = StudentPreferences.getDefaultTeachingScheduled(selectedPreferences.teachingPeriod, selectedPreferences.summerWinter)
      const startTeachingScheduled = [{ year: 1, teachingSchedule: StudentPreferences.getStartTeachingSchedule(selectedPreferences.teachingPeriod, selectedPreferences.summerWinter) }]
      const defaultWorkload = StudentPreferences.courseloopOptionToStudyOption(selectedPreferences.studyOption).workload
      const completedUnits = rootGetters['units/getCompletedUnits']
      const selectedItems = rootGetters['units/getSelectedCurriculumItems']
      const startYear = selectedPreferences.startYear
      const validLocations = StudentPreferences.getCourseLocations(course, selectedPreferences.studentType, selectedPreferences.placeOfResidence)
      const teachingPeriods = rootGetters['preferences/getTeachingPeriods']
      const selectedUnitCodes = rootGetters['units/getSelectedUnitCodes']
      const workLoad = getters['getWorkLoad']
      const lockedUnits = getters['getLockedUnits']

      return inputBuilder.createInput(
         defaultTeachingScheduled,
         startTeachingScheduled,
         defaultWorkload,
         workLoad,
         lockedUnits,
         completedUnits,
         selectedItems,
         startYear,
         validLocations,
         teachingPeriods,
         selectedUnitCodes
      )
   },
   getStudentNotes: state => state.studentNotes,
   getPlanHash: state => state.hash,
   getPlanData: state => state.plan,
   getLockedUnits: (state, getters, rootState, rootGetters) => {
      //only returned locked units IF they fit the current selected teaching period.
      const selectedPreferences = rootGetters['preferences/getSelectedStudyPreferences']
      const defaultTeachingScheduled = StudentPreferences.getDefaultTeachingScheduled(selectedPreferences.teachingPeriod, selectedPreferences.summerWinter)
      return state.lockedUnits.filter(x => (defaultTeachingScheduled.some(y => y == x.teachingPeriod)))
   },
   getWorkLoad: state => state.plan?.years.map((x,index) => x.teachingPeriods
                                                             .map(x=>{
                                                                return {year:index+1, teachingPeriod: x.teachingScheduled, workload: x.workload}
                                                             }))
                                          .flat() || [],
   getAllocatedUnitIds: (state, getters) => {
      if (!getters['getPlanData']) {
        return []
      }
      return getters['getPlanData'].years.reduce((arr, year) => {
        const units = year.teachingPeriods.reduce((periodUnits, tp) => {
          periodUnits = [...periodUnits, ...tp.units.map((u) => u.id)]
          return periodUnits
        }, [])
        arr = [...arr, ...units]
        return arr
      }, [])
    },
}

const mutations = {

   setPlan: (state, planObj) => state.plan = planObj,
   setHash: (state, hash) => state.hash = hash,
   setLockedUnits: (state, lockedUnits) => state.lockedUnits = lockedUnits,
   setStudentNotes: (state, studentNotes) => state.studentNotes = studentNotes,

   resetState: (state) => {
      Object.assign(state, getNewStateObj())
   }
}

const actions = {
   clearHash: ({commit}) => commit('setHash',null),

   async updateAndValidatePlan({commit, getters, dispatch, rootGetters}, {coursePlan}) {
      let selectedCurriculumUnits = rootGetters['units/getSelectedCurriculumItems']

      const selectedPreferences = rootGetters['preferences/getSelectedStudyPreferences']
      const startYear = selectedPreferences.startYear


      const course = rootGetters['courses/getSelectedCourse']
      const validLocations = StudentPreferences.getCourseLocations(course, selectedPreferences.studentType, selectedPreferences.placeOfResidence)

      const defaultTeachingScheduled = StudentPreferences.getDefaultTeachingScheduled(selectedPreferences.teachingPeriod, selectedPreferences.summerWinter)
      const teachingPeriods = rootGetters['preferences/getTeachingPeriods']

      const convertedUnits = inputBuilder.convertUnits(selectedCurriculumUnits, startYear, validLocations, teachingPeriods, defaultTeachingScheduled)

      // loop thru coursePlan.years.teachingPeriods.units, updating the units offerings, prereqs with loaded units defaultofferings, offerings and prereqs
      for (let yearIndex in coursePlan.years) {
         let year = coursePlan.years[yearIndex]
         for (let teachingPeriodIndex in year.teachingPeriods) {
            let teachingPeriod = year.teachingPeriods[teachingPeriodIndex]
            for (let unitIndex in teachingPeriod.units) {
               let selectedUnit = teachingPeriod.units[unitIndex]
               let unit = convertedUnits.find((item) => item.id === selectedUnit.id)
               if (unit) {
                  selectedUnit.offerings = unit.offerings
                  selectedUnit.requisites = unit.requisites
                  selectedUnit.prerequisites = unit.prerequisites
                  selectedUnit.defaultOfferings = unit.defaultOfferings
               }
            }
         }
      }

      // how does this not throw errors, but fixing the units to actually have proper structure throws 250+ errors?
      // if no offerings, validate will throw the same error as our above check, they are not mutually exclusive
      // i.e. no need to check if unitErrors has length > 0, it can only be greater than 0, if errorList is also greater > 0
      let errorList = await CoursePlanStore.validate(coursePlan)
      commit('setPlan', coursePlan)
      if (errorList.length > 0 && errorList !== null) {
         throw errorList
      }
   },
   /*
    generateCoursePlan: Runs the generation of the course plan, if the input is different to the last generated plan.
    Parameters:
      doForceGenerate - If set to true will override the input check to force the regeneration of the plan.
    Return:             Will return a bool value for if the generated course plan correctly planned all the locked units
   */
    async generateCoursePlan({ commit, getters, rootState, rootGetters }, { doForceGenerate, throwErrorOnNewErrors }) {

      let coursePlan = null
      let completed = false
      const input = getters['getInput']
      const currentPlan = getters['getPlanData']
      const courseHash = objectHash(input)
      if (getters['getPlanHash'] !== courseHash || doForceGenerate) { // hash difference - generate will calculate new values
         try {
            // return core selections
            let summary = {
               courseCode: rootGetters['courses/getSelectedCourse'].code,
               selection: rootGetters['units/getSelectedCurriculumItems'].filter(item => item.relationship.type !== 'Unit').map(sel => sel.relationship.code).filter(f=>f)
            }
            console.log('CONTEXT', summary)
            console.log('INPUT', input)

            let currentSiloedUnits = [];
            //ensure regenerations do not add more silo'ed units. otherwise the user shouldnt move units around.
            if (currentPlan != null)
            {
               currentSiloedUnits = currentPlan?.siloedUnits?.map(u => u.code)
            }

            if (input.units.length == 0)
            {
               // Only set if no errors
               commit('setPlan', {years: [], nonScheduledUnits: input.nonScheduledUnits})
               commit('setHash', courseHash)
               return;
            }
            const response = await CoursePlanBuilder.generateCoursePlan(input, summary)
            if (response && response.response) {
               coursePlan = response.response
               completed = true
               console.log('OUTPUT', coursePlan)
            }

            //only test silo units if we are regenerating due to movement of units.
            if (throwErrorOnNewErrors && coursePlan.siloedUnits.length > currentSiloedUnits.length)
            {
               // What are the new siloed units?
               let newSiloed = coursePlan.siloedUnits.map(u => u.code).filter(u => !currentSiloedUnits.includes(u))
               // Reset the plan back to what it was if there was an error
               let err = new Error(`Increase in Siloed Units: ${newSiloed.join(',')}`)
               err.context = newSiloed.length ? newSiloed : null
               throw err
            }

            // Only set if no errors
            commit('setPlan', coursePlan)
            commit('setHash', courseHash)
         } catch (err) {
            throw (err)
         }
      }
      else {
         console.log('Same input - NOT recalcuting course plan')
      }
    },

    unitLockSet({commit, getters}, {code, year, teachingPeriod}){
      let lockedUnits = getters['getLockedUnits']
      if(code.length > 0 && year.length > 0 && teachingPeriod.length > 0){
         // create dictionary
         const dicUnits = Object.assign({}, ...lockedUnits.map(x => ({[x.code]: {code: x.code, year: x.year, teachingPeriod: x.teachingPeriod}})))
         dicUnits[code] = {code, year, teachingPeriod}

         commit('setLockedUnits', Object.values(dicUnits))
         return true;
      }
      return false;
    },

    unitLockClear({commit, getters}, code){
      let lockedUnits = getters['getLockedUnits']
      lockedUnits = lockedUnits.filter(x => x.code != code)
      commit('setLockedUnits', lockedUnits)
    },
}

export default {
   namespaced: true,
   state: getNewStateObj(),
   getters,
   actions,
   mutations,
   getNewStateObj
}
